import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

const MIN_DURATIONS = {
  germination: 3,
  seedling: 7,
  maturing: 7,
  default: 1
}

/**
 * Custom hook for dragging phase boundaries on succession child rows.
 *
 * Mirrors usePhaseDrag but scoped to a specific succession childId.
 * handleDragStart receives (childId, phases, intersectionIndex, clientX).
 * hoveredState / draggingState are { childId, intersectionIndex } objects
 * so hits from different children don't collide.
 *
 * @param {Object} options
 * @param {Function} options.onSuccessionPhaseAdjust - (childId, phases) => void
 * @returns {Object} { isDragging, draggingState, hoveredState, setHoveredState, handleDragStart }
 */
export const useSuccessionDrag = ({ onSuccessionPhaseAdjust }) => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    childId: null,
    intersectionIndex: null,
    startX: 0,
    startPhases: null,
  })

  const [hoveredState, setHoveredState] = useState(null) // { childId, intersectionIndex } | null

  const dragStateRef = useRef(dragState)
  const onAdjustRef = useRef(onSuccessionPhaseAdjust)

  useEffect(() => {
    dragStateRef.current = dragState
    onAdjustRef.current = onSuccessionPhaseAdjust
  }, [dragState, onSuccessionPhaseAdjust])

  const handleDragStart = useCallback((childId, phases, intersectionIndex, clientX) => {
    if (!phases || phases.length === 0) return
    setDragState({
      isDragging: true,
      childId,
      intersectionIndex,
      startX: clientX,
      startPhases: phases.map(p => ({
        ...p,
        _startDuration: parseIntervalToDays(p.est_duration),
      })),
    })
  }, [])

  const handleDragMove = useCallback((e) => {
    const state = dragStateRef.current
    if (!state.isDragging || !state.startPhases) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)
    if (deltaDays === 0) return

    let updatedPhases
    if (state.intersectionIndex === 'start') {
      updatedPhases = applyStartDrag(state.startPhases, deltaDays)
    } else if (state.intersectionIndex === 'end') {
      updatedPhases = applyEndDrag(state.startPhases, deltaDays)
    } else {
      updatedPhases = applyIntersectionDrag(state.startPhases, state.intersectionIndex, deltaDays)
    }

    if (!updatedPhases) return
    const cleanedPhases = updatedPhases.map(({ _startDuration, ...p }) => p)
    onAdjustRef.current?.(state.childId, cleanedPhases)
  }, [])

  const handleDragEnd = useCallback(() => {
    setDragState({ isDragging: false, childId: null, intersectionIndex: null, startX: 0, startPhases: null })
  }, [])

  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)
      return () => {
        document.removeEventListener('mousemove', handleDragMove)
        document.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [dragState.isDragging, handleDragMove, handleDragEnd])

  return {
    isDragging: dragState.isDragging,
    draggingState: dragState.isDragging
      ? { childId: dragState.childId, intersectionIndex: dragState.intersectionIndex }
      : null,
    hoveredState,
    setHoveredState,
    handleDragStart,
  }
}

function applyStartDrag(startPhases, deltaDays) {
  const firstPhase = startPhases[0]
  if (!firstPhase) return null
  const min = MIN_DURATIONS[firstPhase.phase_type_code] || MIN_DURATIONS.default
  const newDuration = firstPhase._startDuration - deltaDays
  if (newDuration < min) return null
  const origEnd = new Date(firstPhase.est_started_at)
  origEnd.setDate(origEnd.getDate() + firstPhase._startDuration)
  const newStart = new Date(origEnd)
  newStart.setDate(newStart.getDate() - newDuration)
  return startPhases.map((p, idx) =>
    idx === 0
      ? { ...p, est_started_at: newStart.toISOString(), est_duration: `${newDuration} days` }
      : p
  )
}

function applyEndDrag(startPhases, deltaDays) {
  const lastIdx = startPhases.length - 1
  const lastPhase = startPhases[lastIdx]
  const min = MIN_DURATIONS[lastPhase.phase_type_code] || MIN_DURATIONS.default
  const newDuration = lastPhase._startDuration + deltaDays
  if (newDuration < min) return null
  return startPhases.map((p, idx) =>
    idx === lastIdx ? { ...p, est_duration: `${newDuration} days` } : p
  )
}

function applyIntersectionDrag(startPhases, intersectionIndex, deltaDays) {
  const left = startPhases[intersectionIndex]
  const right = startPhases[intersectionIndex + 1]
  if (!left || !right) return null

  let newLeft = left._startDuration + deltaDays
  let newRight = right._startDuration - deltaDays

  const leftMin = MIN_DURATIONS[left.phase_type_code] || MIN_DURATIONS.default
  const rightMin = MIN_DURATIONS[right.phase_type_code] || MIN_DURATIONS.default

  if (newLeft < leftMin) { newRight += (leftMin - newLeft); newLeft = leftMin }
  if (newRight < rightMin) { newLeft -= (rightMin - newRight); newRight = rightMin }
  if (newLeft < leftMin || newRight < rightMin) return null

  const rightStart = new Date(startPhases[intersectionIndex].est_started_at)
  rightStart.setDate(rightStart.getDate() + newLeft)
  const rightEnd = new Date(rightStart)
  rightEnd.setDate(rightEnd.getDate() + newRight)

  return startPhases.map((p, idx) => {
    if (idx < intersectionIndex) return p
    if (idx === intersectionIndex) return { ...p, est_duration: `${newLeft} days` }
    if (idx === intersectionIndex + 1) return { ...p, est_started_at: rightStart.toISOString(), est_duration: `${newRight} days` }
    let cumStart = rightEnd
    for (let i = intersectionIndex + 2; i < idx; i++) {
      cumStart = new Date(cumStart)
      cumStart.setDate(cumStart.getDate() + startPhases[i]._startDuration)
    }
    return { ...p, est_started_at: cumStart.toISOString() }
  })
}
